import { Button } from '@evaneos/b2b-ui/legacy';
import cx from 'classnames';
import { useRef } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import ReactTooltip from 'react-tooltip';
import { CSSTransition } from 'react-transition-group';

import { ERROR, PENDING, SUCCESS } from '../../../../design-system/constants';
import { ChevronRight } from '../../../../design-system/Icons/ChevronRight';
import { Close } from '../../../../design-system/Icons/Close';
import { Download } from '../../../../design-system/Icons/Download';
import { Message as MessageIcon } from '../../../../design-system/Icons/Message';
import { Loader } from '../../../../design-system/Loader';
import {
    getDiscussionAttachmentsRequest,
    openAttachmentsPanel,
} from '../../../../resources/discussion/actions';
import {
    getDiscussionAttachments,
    getDiscussionAttachmentsStatus,
    shouldOpenAttachmentsPanel,
} from '../../../../resources/discussion/selectors';
import { MessageAttachment, AttachmentType } from '../../../../resources/discussion/types';
import { DossierId } from '../../../../resources/dossier/types';
import { useAppDispatch, useAppSelector } from '../../../../shared/redux';

import s from './AttachmentsRightPanel.css';

interface Props {
    dossierId: DossierId;
    isSidePanelOpen?: boolean;
}

export const AttachmentsRightPanel = ({ dossierId, isSidePanelOpen }: Props) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const location = useLocation();
    const nodeRef = useRef<HTMLDivElement>(null);

    const isDisplayed = useAppSelector(shouldOpenAttachmentsPanel);
    const attachmentsStatus = useAppSelector(getDiscussionAttachmentsStatus(dossierId));
    const attachments = useAppSelector(getDiscussionAttachments(dossierId));

    const tipiLinks = attachments.filter((attachment) => attachment.type === AttachmentType.TIPI);
    const documents = attachments.filter((attachment) => attachment.type !== AttachmentType.TIPI);

    const renderAttachments: JSX.Element = (
        <div className={s.rightPanelBody}>
            <div className={s.section}>
                <div className="title-m">
                    {intl.formatMessage(
                        {
                            id: 'discussion.attachments_list.tipi_proposals_section_title',
                        },
                        { count: tipiLinks.length },
                    )}
                </div>
                {tipiLinks.map((attachment: MessageAttachment) => (
                    <div
                        className={s.attachmentRow}
                        key={attachment.link}
                        data-testid="attachment-tipi-link"
                    >
                        <div className={cx('body', s.textEllipsis)} title={attachment.name}>
                            {attachment.name}
                        </div>
                        <a
                            href={attachment.link}
                            target="_blank"
                            rel="noreferrer noopener"
                            className={s.tipiLink}
                        >
                            {intl.formatMessage({
                                id: 'discussion.attachments_list.see_in_tipi_link',
                            })}
                        </a>
                    </div>
                ))}
                {tipiLinks.length === 0 && (
                    <div className={cx('body', s.sectionEmpty)}>
                        {intl.formatMessage({
                            id: 'discussion.attachments_list.tipi_proposals_empty',
                        })}
                    </div>
                )}
            </div>
            <div className={s.section}>
                <div className="title-m">
                    {intl.formatMessage(
                        {
                            id: 'discussion.attachments_list.documents_section_title',
                        },
                        { count: documents.length },
                    )}
                </div>
                {documents.map((attachment: MessageAttachment) => (
                    <div
                        className={s.attachmentRow}
                        key={attachment.link}
                        data-testid="attachment-document"
                    >
                        <div className={s.attachmentType}>{attachment.type}</div>
                        <div className={cx('body', s.textEllipsis)} title={attachment.name}>
                            {attachment.name}
                        </div>
                        <a
                            href={`#message-${attachment.messageId}`}
                            className={cx(s.downloadButton, s.tertiaryButton, s.messageIconLink)}
                            data-tip={true}
                            data-for="see-message-btn"
                        >
                            <MessageIcon
                                color={
                                    location.hash === `#message-${attachment.messageId}`
                                        ? 'var(--lime)'
                                        : 'var(--ink)'
                                }
                            />
                        </a>
                        <ReactTooltip
                            id="see-message-btn"
                            className={s.tooltip}
                            place="bottom"
                            effect="solid"
                            backgroundColor="var(--ink)"
                            getContent={() =>
                                intl.formatMessage({
                                    id: 'discussion.attachments_list.see_message_tooltip',
                                })
                            }
                        />
                        <a
                            href={attachment.link}
                            className={cx(s.downloadButton, s.tertiaryButton)}
                            data-tip={true}
                            data-for="download-document-btn"
                        >
                            <Download color="var(--ink)" />
                        </a>
                        <ReactTooltip
                            id="download-document-btn"
                            className={s.tooltip}
                            place="bottom"
                            effect="solid"
                            backgroundColor="var(--ink)"
                            getContent={() =>
                                intl.formatMessage({
                                    id: 'global.download',
                                })
                            }
                        />
                    </div>
                ))}
                {documents.length === 0 && (
                    <div className={cx('body', s.sectionEmpty)}>
                        {intl.formatMessage({
                            id: 'discussion.attachments_list.documents_empty',
                        })}
                    </div>
                )}
            </div>
        </div>
    );

    const renderAttachmentsLoading = (
        <div className={s.loader}>
            <Loader />
            <span className={cx('title-s', s.loading)}>
                {intl.formatMessage({
                    id: 'discussion.attachments_list.loading',
                })}
            </span>
        </div>
    );

    const renderAttachmentsError: JSX.Element = (
        <div className={s.error}>
            <div>
                <div className="title-s">
                    {intl.formatMessage({
                        id: 'discussion.attachments_list.error_title',
                    })}
                </div>
                <p>
                    {intl.formatMessage({
                        id: 'discussion.attachments_list.error_description',
                    })}
                </p>
            </div>
            <Button
                priority="primary"
                onClick={() => dispatch(getDiscussionAttachmentsRequest(dossierId))}
            >
                {intl.formatMessage({ id: 'global.retry' })}
            </Button>
        </div>
    );

    return (
        <CSSTransition
            in={isDisplayed}
            appear={true}
            timeout={{ appear: 0, enter: 0, exit: 250 }}
            classNames={{
                enterDone: s.enterDone,
                exit: s.exit,
            }}
            mountOnEnter
            unmountOnExit
            nodeRef={nodeRef}
        >
            <div
                ref={nodeRef}
                className={cx(s.rightPanel, {
                    [s.sidePanelOpen]: isSidePanelOpen,
                })}
                data-testid="attachments-panel"
            >
                <div className={s.rightPanelContent}>
                    <div className={s.panelTopBar}>
                        <div
                            className={cx(s.closePanelButton, s.tertiaryButton)}
                            onClick={() => {
                                dispatch(openAttachmentsPanel(false));
                            }}
                        >
                            <ChevronRight color="var(--ink)" className={s.desktop} />
                            <p>
                                {intl.formatMessage({
                                    id: 'global.close_notification',
                                })}
                            </p>
                            <Close color="var(--ink)" className={s.mobile} />
                        </div>
                    </div>
                    {attachmentsStatus === PENDING && renderAttachmentsLoading}
                    {attachmentsStatus === SUCCESS && renderAttachments}
                    {attachmentsStatus === ERROR && renderAttachmentsError}
                </div>
            </div>
        </CSSTransition>
    );
};
